/**
 * Cascading Font Dropdown Component
 * 
 * Google Docs-style font selector with:
 * - Main dropdown showing font families
 * - Hover flyout showing font variants (Regular, Bold, Italic, etc.)
 * - Each variant rendered in its actual font style as preview
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, ChevronRightIcon, CheckIcon } from '@heroicons/react/24/outline';
import { type FontFamily, type EnabledFont } from '../../../wordprocessing/services/FontAPI';

interface CascadingFontDropdownProps {
  fontFamilies: FontFamily[];
  selectedFamily: string;
  selectedWeight: number;
  selectedStyle: string;
  onSelect: (family: string, weight: number, style: string) => void;
  disabled?: boolean;
}

export const CascadingFontDropdown: React.FC<CascadingFontDropdownProps> = ({
  fontFamilies,
  selectedFamily,
  selectedWeight,
  selectedStyle,
  onSelect,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredFamily, setHoveredFamily] = useState<string | null>(null);
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const familyRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHoveredFamily(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate submenu position based on hovered family
  const handleFamilyHover = (familyName: string) => {
    setHoveredFamily(familyName);
    
    const familyElement = familyRefs.current.get(familyName);
    if (familyElement && dropdownRef.current) {
      const rect = familyElement.getBoundingClientRect();
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      
      setSubmenuPosition({
        top: rect.top - dropdownRect.top,
        left: dropdownRect.width
      });
    }
  };

  const handleVariantSelect = (family: string, weight: number, style: string) => {
    onSelect(family, weight, style);
    setIsOpen(false);
    setHoveredFamily(null);
  };

  // Get display name for current selection
  const getCurrentDisplayName = () => {
    const family = fontFamilies.find(f => f.familyName === selectedFamily);
    if (!family) return selectedFamily;
    
    const variant = family.variants.find(v => v.weight === selectedWeight && v.style === selectedStyle);
    return variant?.displayName || selectedFamily;
  };

  // Format variant name (e.g., "Regular", "Bold", "Italic", "Bold Italic")
  const getVariantName = (variant: EnabledFont): string => {
    if (variant.weight === 400 && variant.style === 'normal') return 'Regular';
    
    const weightName = variant.weight === 700 ? 'Bold' : 
                      variant.weight === 300 ? 'Light' :
                      variant.weight === 500 ? 'Medium' :
                      variant.weight === 600 ? 'SemiBold' :
                      variant.weight === 800 ? 'ExtraBold' :
                      variant.weight === 900 ? 'Black' : '';
    
    const styleName = variant.style === 'italic' ? 'Italic' : '';
    
    return `${weightName} ${styleName}`.trim() || 'Regular';
  };

  const hoveredFamilyData = hoveredFamily ? fontFamilies.find(f => f.familyName === hoveredFamily) : null;

  return (
    <div ref={dropdownRef} className="relative inline-block">
      {/* Main Trigger Button */}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex items-center justify-between
          w-[180px] px-3 py-1.5
          bg-white dark:bg-gray-800
          border border-gray-300 dark:border-gray-600
          rounded-md
          text-sm text-gray-900 dark:text-gray-100
          hover:bg-gray-50 dark:hover:bg-gray-750
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors
        `}
        style={{ fontFamily: selectedFamily }}
      >
        <span className="truncate">{getCurrentDisplayName()}</span>
        <ChevronDownIcon className={`h-4 w-4 ml-2 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Main Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-[280px] max-h-[400px] overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg">
          {fontFamilies.map((family) => {
            const isSelected = family.familyName === selectedFamily;
            // Hide disabled variants at the family level
            const enabledVariants = (family.variants || []).filter(v => v.enabled !== false);
            if (enabledVariants.length === 0) return null;
            
            return (
              <div
                key={family.familyName}
                ref={(el) => {
                  if (el) familyRefs.current.set(family.familyName, el);
                }}
                onMouseEnter={() => handleFamilyHover(family.familyName)}
                onMouseLeave={() => enabledVariants.length === 1 && setHoveredFamily(null)}
                onClick={() => {
                  // If only one variant, select it immediately
                  if (enabledVariants.length === 1) {
                    const variant = enabledVariants[0];
                    handleVariantSelect(family.familyName, variant.weight, variant.style);
                  }
                }}
                className={`
                  relative flex items-center justify-between
                  px-4 py-2.5
                  cursor-pointer
                  hover:bg-gray-50 dark:hover:bg-gray-750
                  ${isSelected ? 'bg-lex-accent-soft' : ''}
                  transition-colors
                `}
              >
                <div className="flex items-center flex-1 min-w-0">
                  <span
                    className="text-sm truncate"
                    style={{ fontFamily: family.familyName }}
                  >
                    {family.familyName}
                  </span>
                  {family.variants[0].isCustom && (
                    <span className="ml-2 px-1.5 py-0.5 text-xs bg-lex-accent-soft text-lex-accent rounded">
                      Custom
                    </span>
                  )}
                </div>
                
                {enabledVariants.length > 1 && (
                  <ChevronRightIcon className="h-4 w-4 ml-2 text-gray-400 flex-shrink-0" />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Variant Submenu (Flyout) */}
      {isOpen && hoveredFamilyData && hoveredFamilyData.variants.filter(v => v.enabled !== false).length > 1 && (
        <div
          className="absolute z-50 w-[240px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg"
          style={{
            top: submenuPosition.top,
            left: `calc(280px + 4px)`, // Main dropdown width + gap
            maxHeight: '400px',
            overflowY: 'auto'
          }}
        >
          {hoveredFamilyData.variants.filter(v => v.enabled !== false).map((variant) => {
            const isSelected = 
              variant.fontFamily === selectedFamily &&
              variant.weight === selectedWeight &&
              variant.style === selectedStyle;
            
            return (
              <button
                key={`${variant.fontUid}`}
                onClick={() => handleVariantSelect(variant.fontFamily, variant.weight, variant.style)}
                className={`
                  w-full flex items-center justify-between
                  px-4 py-2.5
                  text-left text-sm
                  hover:bg-gray-50 dark:hover:bg-gray-750
                  ${isSelected ? 'bg-lex-accent-soft' : ''}
                  transition-colors
                `}
              >
                <span
                  className="flex-1 truncate"
                  style={{
                    fontFamily: variant.fontFamily,
                    fontWeight: variant.weight,
                    fontStyle: variant.style
                  }}
                >
                  {getVariantName(variant)}
                </span>
                {isSelected && (
                  <CheckIcon className="h-4 w-4 ml-2 text-lex-accent flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

